import React from 'react'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import { Button } from '../../../../components/Button'
import { RiEdit2Line, RiDeleteBin6Line, RiEyeLine } from 'react-icons/ri'
import { FaRegBuilding, FaUserShield, FaUsers, FaServer } from 'react-icons/fa'
import { HiExclamationTriangle } from 'react-icons/hi2'
import type { Processo, Setor, Autorizador, Sistema } from '../../../../models'
import { ProcessoViewCard } from './ProcessoViewCard.tsx'
import { SetorViewCard } from './SetorViewCard.tsx'
import { AutorizadorViewCard } from './AutorizadorViewCard.tsx'
import { SistemaViewCard } from './SistemaViewCard.tsx'
import { cn } from '../../../../utils/formatter'

export type ViewModalType = 'processos' | 'setores' | 'equipes' | 'autorizadores' | 'sistemas'

// Tipo para autorizadores que agora são setores
type SetorAutorizador = {
  id: number
  nome_setor: string
  equipe?: boolean
  chefe_setor_fk?: number | null
  active?: boolean
  pivot?: {
    processo_fk: number
    autorizador_fk: number
    ordem: number
    active: boolean
    created_at?: string
    updated_at?: string
  }
}

interface ViewModalProps {
  isOpen: boolean
  onClose: () => void
  type: ViewModalType | null
  item: Processo | Setor | Autorizador | Sistema | null
  onEdit?: () => void
  onDelete?: () => void
  canEdit?: boolean
  canDelete?: boolean
  // Navegação entre entidades
  onNavigateToProcesso?: (processo: Processo) => void
  onNavigateToSetor?: (setor: Setor) => void
  onNavigateToAutorizador?: (autorizador: Autorizador | SetorAutorizador) => void
  // Estado de exclusão
  isDeleting?: boolean
}

export const ViewModal: React.FC<ViewModalProps> = ({
  isOpen,
  onClose,
  type,
  item,
  onEdit,
  onDelete,
  canEdit = false,
  canDelete = false,
  onNavigateToProcesso,
  onNavigateToSetor,
  isDeleting = false
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)

  React.useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false)
    }
  }, [isOpen])

  if (!type || !item) return null

  const getIcon = () => {
    switch (type) {
      case 'processos':
        return <RiEyeLine className="w-8 h-8 text-blue-500" />
      case 'setores':
        return <FaRegBuilding className="w-8 h-8 text-blue-500" />
      case 'equipes':
        return <FaUsers className="w-8 h-8 text-purple-500" />
      case 'autorizadores':
        return <FaUserShield className="w-8 h-8 text-blue-500" />
      case 'sistemas':
        return <FaServer className="w-8 h-8 text-blue-500" />
      default:
        return <RiEyeLine className="w-8 h-8 text-blue-500" />
    }
  }

  const getTitle = () => {
    switch (type) {
      case 'processos':
        return (item as Processo).nome_processo
      case 'setores':
      case 'equipes':
        return (item as Setor).nome_setor
      case 'autorizadores':
        return (item as Autorizador).nome
      case 'sistemas':
        return (item as Sistema).nome
      default:
        return 'Visualização'
    }
  }


  const getDescription = () => {
    switch (type) {
      case 'processos':
        return 'Detalhes completos do processo cadastrado'
      case 'setores':
        return 'Detalhes completos do setor'
      case 'equipes':
        return 'Detalhes completos da equipe'
      case 'autorizadores':
        return 'Detalhes completos do autorizador'
      case 'sistemas':
        return 'Detalhes completos do sistema de TI'
      default:
        return 'Visualização de detalhes'
    }
  }

  const renderCard = () => {
    switch (type) {
      case 'processos':
        return (
          <ProcessoViewCard
            processo={item as Processo}
            {...(onNavigateToSetor && { onNavigateToSetor })}
          />
        )
      case 'setores':
      case 'equipes':
        return (
          <SetorViewCard
            setor={item as Setor}
            isEquipe={type === 'equipes'}
            {...(onNavigateToProcesso && { onNavigateToProcesso })}
          />
        )
      case 'autorizadores':
        return (
          <AutorizadorViewCard
            autorizador={item as Autorizador}
            {...(onNavigateToProcesso && { onNavigateToProcesso })}
          />
        )
      case 'sistemas':
        return <SistemaViewCard sistema={item as Sistema} />
      default:
        return null
    }
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete()
    }
  }


  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      description={getDescription()}
      icon={getIcon()}
      size="xl"
      useStandardLayout
      resizable={true}
    >
      {renderCard()}

      {/* Footer Actions */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-neutral-700">
        {showDeleteConfirm ? (
          <div className="p-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <HiExclamationTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                    Confirmar exclusão?
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                    Esta ação não pode ser desfeita.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className={cn(
                        'px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs font-medium flex items-center gap-1.5',
                        isDeleting && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <RiDeleteBin6Line className="w-3.5 h-3.5" />
                      {isDeleting ? 'Excluindo...' : 'Sim, excluir'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-xs font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 p-4">
            {/* Delete button */}
            {canDelete ? (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
              >
                <RiDeleteBin6Line className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            ) : (
              <div />
            )}

            {/* Edit/Close buttons */}
            <div className="flex items-center gap-3 ml-auto">
              <Button
                variant="outline"
                onClick={onClose}
                className="min-w-[100px]"
              >
                Fechar
              </Button>
              {canEdit && onEdit && (
                <Button
                  onClick={onEdit}
                  className="min-w-[100px]"
                >
                  <RiEdit2Line className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  )
}

